// StagesPage — CRM /stages admin page (#1080, updated by #1958 Phase 2).
//
// @aiPurpose lists the locked seven canonical Lead stages and lets a
//   `stages.manage` admin edit displayName only. `stages.read` users
//   see the same list but no Edit button. code/kind/sortOrder remain
//   locked read-only; taskTitle/defaultDueMinutes are backend
//   compatibility fields not exposed in the web UI (#1956 Phase 2).
// @aiInvariant CREATE / DELETE / REORDER / DISABLE / kind- or code-
//   change UI is intentionally absent. Adding any such surface must
//   trip the issue scope review per #1080.
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { LeadStage } from '@magimp/shared';
import { Button, DataTable, EmptyState, ErrorState, LoadingState } from '../../../ui/index.js';
import { errorMessage } from '../../../api/errors.js';
import { useToastStore } from '../../../stores/toast.js';
import { hasPermission, useAuthStore } from '../../../stores/auth.js';
import { useLeadStages } from '../queries.js';
import { StageEditModal } from './StageEditModal.js';

export function StagesPage() {
  const { t } = useTranslation(['stages', 'common']);
  const permissions = useAuthStore((s) => s.permissions);
  const canManage = hasPermission(permissions, 'stages.manage');
  const show = useToastStore((s) => s.show);
  const [editStage, setEditStage] = useState<LeadStage | null>(null);

  const list = useLeadStages();

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, minHeight: 0, flex: 1 }}>
      <header style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <h1 style={{ fontSize: 20, fontWeight: 600, margin: 0, flex: 1 }}>{t('stages:title')}</h1>
        <span style={{ fontSize: 12, color: 'var(--fg-3)' }}>{t('stages:hint.lockedCatalog')}</span>
      </header>

      {list.error && (
        <ErrorState
          title={t('common:states.loadError')}
          text={errorMessage(list.error)}
          onRetry={() => void list.refetch()}
        />
      )}
      {list.isPending && <LoadingState />}
      {list.isSuccess && (
        <DataTable<LeadStage>
          rows={list.data}
          getRowKey={(s) => s.code}
          emptyState={<EmptyState title={t('stages:empty.title')} text={t('stages:empty.text')} />}
          columns={[
            { key: 'sortOrder', label: t('stages:columns.sortOrder'), width: 70 },
            {
              key: 'code',
              label: t('stages:columns.code'),
              render: (s) => <code data-testid={`stage-row-${s.code}`}>{s.code}</code>,
            },
            {
              key: 'kind',
              label: t('stages:columns.kind'),
              render: (s) => t(`stages:kind.${s.kind}`),
            },
            { key: 'displayName', label: t('stages:columns.displayName') },
            {
              key: 'actions',
              label: '',
              align: 'right',
              render: (s) =>
                canManage ? (
                  <Button
                    size="sm"
                    onClick={() => setEditStage(s)}
                    data-testid={`stage-edit-${s.code}`}
                  >
                    {t('common:actions.edit')}
                  </Button>
                ) : null,
            },
          ]}
        />
      )}

      <StageEditModal
        open={editStage != null}
        stage={editStage}
        onClose={() => setEditStage(null)}
        onUpdated={() => show({ tone: 'success', text: t('stages:toasts.updated') })}
      />
    </div>
  );
}
